import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  LinearProgress,
  Alert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import { useReplay } from '@/hooks/useReplay.js';
import type { SimulationRun } from '@k-ucap/shared';

const speedMarks = [
  { value: 0.5, label: '0.5x' },
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 4, label: '4x' },
];

export const ReplayDialog = ({
  run,
  open,
  onClose,
}: {
  run: SimulationRun | null;
  open: boolean;
  onClose: () => void;
}) => {
  const {
    isPlaying,
    progress,
    currentMessage,
    totalMessages,
    speed,
    error,
    loadMessages,
    play,
    pause,
    stop,
    setPlaybackSpeed,
  } = useReplay();

  useEffect(() => {
    if (open && run) {
      loadMessages(run.id);
    }
  }, [open, run, loadMessages]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replay: {run?.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {run?.executionConfig.recordMode === 'NONE' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Record Mode가 NONE이므로 Replay할 메시지가 없습니다.
          </Alert>
        )}
        {run?.executionConfig.recordMode === 'SUMMARY' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            SUMMARY 모드입니다. 상세 메시지 Replay가 제한됩니다.
          </Alert>
        )}
        <Typography variant="body2">
          Messages: {currentMessage} / {totalMessages}
        </Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={play} disabled={isPlaying}>
            Play
          </Button>
          <Button variant="outlined" startIcon={<PauseIcon />} onClick={pause} disabled={!isPlaying}>
            Pause
          </Button>
          <Button variant="outlined" color="error" startIcon={<StopIcon />} onClick={stop}>
            Stop
          </Button>
        </Box>
        <Typography variant="body2" gutterBottom>
          Playback Speed: {speed}x
        </Typography>
        <Slider
          value={speed}
          onChange={(_, value) => setPlaybackSpeed(value as number)}
          step={0.5}
          marks={speedMarks}
          min={0.5}
          max={4}
          valueLabelDisplay="auto"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReplayDialog;
